import { Config } from 'config';
import { ColorEngineBrush } from './brush';
import { forEach, map } from 'lodash';

import { OwnedObject, invalidateOwner } from 'gear';

import { Xml } from '../util';
import { Point } from '../geometry';
import { Color } from './color';

import { rgb2lab, rgb2xyz, rgb2lch, rgb2hsl,
         lch2rgb, lab2rgb, hsl2rgb,
         interpolate_rgb, interpolate_hsl,
         interpolate_lab, interpolate_lch } from './color-space';

const toImageData = function(image) {
  let context;
  if( image instanceof HTMLImageElement ) {
    const canvas = document.createElement('canvas');
    canvas.width  = image.width ;
    canvas.height = image.height;
    context = canvas.getContext('2d');
    context.drawImage(image,0,0);
  }
  else {
    context = image.getContext('2d');
  }
  return context.getImageData(0,0,image.width,image.height);
};

const toCanvas = function(imageData) {
  const canvas = document.createElement('canvas');
  canvas.width  = imageData.width ;
  canvas.height = imageData.height;
  canvas.getContext('2d').putImageData(imageData,0,0);
  return canvas;
};

const _floor = Math.floor, _min = Math.min, _max = Math.max, _sqrt = Math.sqrt;

const interpolate_value = function(l1,l2,a) {
  return l1 + ( l2 - l1 ) * a;
};


// Scale Functions, convert a true color to gray scale using
// different strategies

const rgb2 = {

  CieLightness( r,g,b) {
    // CieLAB color space
    // https://en.wikipedia.org/wiki/Lab_color_space
    return rgb2lab([r,g,b])[0] / 100;
  },

  Luma( r,g,b) {
    // XYZ model using sRGB gamut
    return rgb2xyz([r,g,b])[1] / 100; // 0.2126*r' + 0.7152*g' + 0.722*b'
  },

  Luminance( r,g,b) {
    // Same formula a Luma, but using rgb directly
    // https://en.wikipedia.org/wiki/Luma_(video)
    return ( 2126*r + 7152*g +  722*b ) / (255*10000);
  },

  Brightness( r,g,b) {
    // YIQ color space, W3C working draft
    // https://en.wikipedia.org/wiki/YIQ
    // http://www.w3.org/TR/AERT#color-contrast
    return ( 299*r + 587*g + 114*b )/(255*1000);
  },

  BrightnessP( r,g,b) {
    // Similar to Adobe Photoshop RGB->Grayscale method?
    // This is not standard, slow and doesn't have real info
    // http://alienryderflex.com/hsp.html
    const red = r/255, green = g/255, blue = b/255;
    return _sqrt( 0.299 * (red*red) + 0.587 * (green*green) + 0.114 * (blue*blue) );
  },

  Lightness( r,g,b) {
    // HSL color space
    return ( _max(r,g,b) + _min(r,g,b) ) / ( 2 * 255 );
  },

  Value( r,g,b) {
    // HSV color space
    return _max(r,g,b) / 255;
  },

  Intensity( r,g,b) {
    // HSI color space, just for completeness
    // https://en.wikipedia.org/wiki/HSL_and_HSV
    return ( r+g+b ) / (3*255);
  },

  // Color separation
  Red   ( r/*,g,b*/) { return r/255; },
  Green ( r,g/*,b*/) { return g/255; },
  Blue  ( r,g,b) { return b/255; },

  LCH( r,g,b) { return rgb2lch([r,g,b]); },
  LAB( r,g,b) { return rgb2lab([r,g,b]); },
  HSL( r,g,b) { return rgb2hsl([r,g,b]); },
  RGB( r,g,b) { return [r,g,b]; }
};

const slowScaleFunction = {
  CieLightness : true,
  Luma         : true,
  BrightnessP  : true
};

const createScaleFunction = function(scaleMap,res) {
  let scaleFunction = rgb2[scaleMap];
  if( slowScaleFunction[scaleMap] && res > 0 ) {
    // Avoid converting each pixel to complex color spaces
    scaleFunction = cachedRGBCubeMap( scaleFunction, interpolate_value, res );
  }
  return scaleFunction;
};

const gradient_map         = { LCH : rgb2lch, LAB : rgb2lab, HSL : rgb2hsl, RGB ( c ) { return c; } };
const gradient_mapBack     = { LCH : lch2rgb, LAB : lab2rgb, HSL : hsl2rgb, RGB ( c ) { return c; } };
const gradient_interpolate = {
  LCH : interpolate_lch, LAB : interpolate_lab, HSL : interpolate_hsl, RGB : interpolate_rgb
};

const createRGBGradientFunction = function(gradient,res) {

  // 0...1 to get the gradient RGB at that position
  return function(x) {
    const x_res = x * res, i = _floor(x_res);
    return i <  0  ? gradient[0] :
           i < res ? interpolate_rgb( gradient[i], gradient[i+1], x_res-i ) :
                     gradient[res] ;
  };
};

const createRGBGradient = function(colors,positions,gradientType,res) {
  const gmap        = gradient_map        [gradientType];
  const gmapBack    = gradient_mapBack    [gradientType];
  const interpolate = gradient_interpolate[gradientType];

  const n = colors.length;

  const mappedColors = map(map(colors,'rgbArray'),gmap);
  const rgbGradient = [];
  const first_position = positions[0];
  const last_position = positions[n-1];

  let current = 1;

  for(let k=0 ; k<=res ; ++k) {
    const pos = k/res;
    let rgb;

    if( pos <= first_position ) {
      rgb = colors[0].rgbArray;
    }
    else if( pos >= last_position ) {
      rgb = colors[n-1].rgbArray;
    }
    else {
      while( current < n && positions[current] < pos ) {
        ++current;
      }
      const prev = current-1;
      const positions_current = positions[current],
          positions_prev    = positions[prev];
      const a = ( pos-positions_prev ) / ( positions_current-positions_prev );
      const c = interpolate( mappedColors[prev], mappedColors[current], a );
      rgb = gmapBack(c);
    }

    rgbGradient[k] = rgb;
  }

  return createRGBGradientFunction(rgbGradient,res);
};

const cachedRGBCubeMap = function(map,interpolate,res) {
  const v = [];
  for(let r=0;r<=res;++r) {
    const gb = [];
    for(let g=0;g<=res;++g) {
      gb.push([]);
    }
    v.push(gb);
  }

  const _res_255 = res/255, _255_res = 255/res;

  const _map = function(ri,gi,bi) {
    let rgb = v[ri][gi][bi];
    if( !rgb ) {
      rgb = map( ri * _255_res, gi * _255_res, bi * _255_res );
      v[ri][gi][bi] = rgb;
    }
    return rgb;
  };

  return function(r,g,b) {
    let rc = r * _res_255, gc = g * _res_255, bc = b * _res_255;
    let ri = _floor(rc), gi = _floor(gc), bi = _floor(bc);
    rc -= ri; gc -= gi; bc -= bi;

    // This can be optimized
    if( ri === res ) {
      ri = res-1;
      rc = 1;
    }
    if( gi === res ) {
      gi = res-1;
      gc = 1;
    }
    if( bi === res ) {
      bi = res-1;
      bc = 1;
    }

    const c_000 = _map(ri  ,gi  ,bi  );
    const c_100 = _map(ri+1,gi  ,bi  );
    const c_010 = _map(ri  ,gi+1,bi  );
    const c_110 = _map(ri+1,gi+1,bi  );
    const c_001 = _map(ri  ,gi  ,bi+1);
    const c_101 = _map(ri+1,gi  ,bi+1);
    const c_011 = _map(ri  ,gi+1,bi+1);
    const c_111 = _map(ri+1,gi+1,bi+1);

    const c_x00 = interpolate(c_000,c_100,rc);
    const c_x10 = interpolate(c_010,c_110,rc);
    const c_x01 = interpolate(c_001,c_101,rc);
    const c_x11 = interpolate(c_011,c_111,rc);

    const c_xx0 = interpolate(c_x00,c_x10,gc);
    const c_xx1 = interpolate(c_x01,c_x11,gc);

    const c_xxx = interpolate(c_xx0,c_xx1,bc);

    return c_xxx;
  };
};

// We should drop old cached color maps, changing a lot of colors could consume a lot of memory
const colorMapCache = {};
const colorMapCacheList = [];

const cachedColorMap = function(fun) {
  if( ! Config.colorMapCaching ) {
    return fun;
  }

  return function(image,id ) {
    const cm_id = id+'('+image.width+','+image.height+')['+this.textualize()+']';
    const cachedResult = colorMapCache[cm_id];
    if( cachedResult ) {
      return cachedResult;
    }
    // Not cached, perform the operation
    const result = fun.call(this,image,id);
    colorMapCache[cm_id] = result;
    colorMapCacheList.push(cm_id);
    if( colorMapCacheList.length > Config.colorMapCacheSize ) {
      const old_cm_id = colorMapCacheList.shift();
      delete colorMapCache[old_cm_id];
    }
    return result;
  };
};

// values between 0...255
// map :: function(r,g,b,a){ return [ r*, g*, b*, a* ]; }

const modifyImageDataRGBA = function(imageData,map){
  const data = imageData.data, width = imageData.width, height = imageData.height;
  let y, x, inpos, outpos, r, g, b, a;

  for(y = 0; y < height; ++y) {
    inpos = y * width * 4; // *4 for 4 ints per pixel
    outpos = inpos;
    for(x = 0; x < width; ++x) {

      r = data[inpos++];
      g = data[inpos++];
      b = data[inpos++];
      a = data[inpos++]; // alpha

      // http://www.w3.org/TR/AERT#color-contrast
      const c = map(r,g,b,a);

      data[outpos++] = c[0];
      data[outpos++] = c[1];
      data[outpos++] = c[2];
      data[outpos++] = c[3];
    }
  }
};

// values between 0...255
// map :( r,g,b) { return [ r*, g*, b* ]; }

const modifyImageDataRGB = function(imageData,map) {
  const data = imageData.data, width = imageData.width, height = imageData.height;
  let y, x, inpos, outpos, r, g, b;

  for(y = 0; y < height; ++y) {
    inpos = y * width * 4; // *4 for 4 ints per pixel
    outpos = inpos;
    for(x = 0; x < width; ++x) {

      r = data[inpos++];
      g = data[inpos++];
      b = data[inpos++];

      ++inpos; // alpha

      // http://www.w3.org/TR/AERT#color-contrast
      const c = map(r,g,b);

      data[outpos++] = c[0];
      data[outpos++] = c[1];
      data[outpos++] = c[2];

      ++outpos; // alpha
    }
  }
};


export const ColorMap = OwnedObject.extend({ typeName: 'ColorMap', __debugProps: true,
  Properties: {
    // ...
  },
  init( config ) {
    this.callParent(config);
    // ...
  },
  invalidate: invalidateOwner,
});


// TaggedColors
//======================================================================================


export const TaggedColors = ColorMap.extend({ typeName: 'TaggedColors',

  Properties: {
    colors : { type: 'Color[]', def: [ Color.Black(), Color.White() ], owned: true, onChange: invalidateOwner }
  },

  invalidate: invalidateOwner,

  getColors( colors = [] ) {
    forEach( this.colors, c => { colors.push(c); } );
    return colors;
  },
  textualize() {
    return this._colors.textualize();
  },
  recolorized( thing) {
    return this.recolorize(thing.clone());
  },
  recolorize( thing) {
    const thingColors = thing.getColors();
    const colors = this._colors;
    const toColorMap = {};
    for(let i=0,iEnd=colors.length;i<iEnd;++i) {
      const color = colors[i], tag = color._tag;
      if( tag ) {
        toColorMap[tag] = color;
      }
      // [TODO] else assert!
    }
    for(let k=0,kEnd=thingColors.length;k<kEnd;++k) {
      const tc = thingColors[k];
      const tc_tag = tc._tag;
      if( tc_tag ) {
        const toColor = toColorMap[tc_tag];
        if( toColor ) {
          tc.replace( toColor );
        }
      }
    }
    return thing;
  }

});

Xml.addFromBuild(TaggedColors,'Colors');

// GradientColorMap
//======================================================================================

export const GradientColorMap = ColorMap.extend({ typeName: 'GradientColorMap',

  Properties: {
    positions: { type: 'number[]', def: [ 0, 1 ], onChange:invalidateOwner },
    colors: { type: 'Color[]', def: [ Color.Black(), Color.White() ], owned: true, onChange: invalidateOwner },
    scaleMap: { type: 'string', def: 'CieLightness', onChange:invalidateOwner },
    gradientType: { type: 'string', def: 'LAB', onChange:invalidateOwner }
  },

  invalidate: invalidateOwner,

  getColors( colors = [] ) {
    const this_colors = this._colors;
    //_.forEach(this_colors,function(c) {
    for(let k=0,kEnd=this_colors.length;k<kEnd;++k) {
      const c = this_colors[k];
      colors.push(c);
    }//);
    return colors;
  },
  rgbGradient() {
    // This can be cached
    return createRGBGradient( this._colors, this._positions, this._gradientType, Config.GradientColorMap_scaleRes );
  },
  textualize() {
    return this._colors.textualize() + '|' + this._positions.textualize();
  },
  recolorize: cachedColorMap(function(image) {
    const scaleMap = this._scaleMap;

    const imageData = toImageData(image);

    const scaleFunction = createScaleFunction( scaleMap, Config.GradientColorMap_RGBCubeRes );
    if( scaleFunction ) {

      const rgbGradient = this.rgbGradient();

      modifyImageDataRGB( imageData, (r,g,b) => {
        return rgbGradient( scaleFunction(r,g,b) );
      });
    }
    else {
      console.log('GradientColorMap, Unsupported ScaleMap: '+scaleMap);
    }

    return toCanvas(imageData);
  })
});

Xml.addFromBuild(GradientColorMap,'GradientColorMap');



// MonochromeColors
//======================================================================================

export const MonochromeColors = ColorMap.extend({ typeName: 'MonochromeColors',

  Properties: {
    colors : { type: 'Color[]', def: [ Color.White(), Color.Black() ], owned: true, onChange: invalidateOwner }
  },

  invalidate: invalidateOwner,

  getColors( colors = [] ) {
    const this_colors = this._colors;
    colors.push( this_colors[0] );
    colors.push( this_colors[1] );
    return colors;
  },
  textualize() {
    return this._colors.textualize();
  },

  recolorize: cachedColorMap( function( image ) {

    // This is a gradient color map that also modifies the alpha values of the output
    // We may extend gradient color map to also do this, but it is not clear
    // the best way forward at this point

    const imageData = toImageData(image);

    const scaleFunction = createScaleFunction( 'Brightness', Config.GradientColorMap_RGBCubeRes );

    // Values are inverted in MonochromeColors
    const colors = this._colors;

    let color_0 = colors[1],
        color_1 = colors[0];

    const alpha_0 = color_0._alpha * 255,
          alpha_1 = color_1._alpha * 255;

    // Discard garbage rgb value for the special case of total transparency
    if( alpha_0 === 0 ){
      color_0 = color_1;
    }
    if( alpha_1 === 0 ){
      color_1 = color_0;
    }

    const rgbGradient = createRGBGradient( [ color_0, color_1 ], [0,1], 'LAB', Config.GradientColorMap_scaleRes );

    modifyImageDataRGBA( imageData, (r,g,b /*,a*/) => {
      const value = scaleFunction(r,g,b);
      const c = rgbGradient(value);

      // Interpolate alpha
      c[3] = alpha_0 * (1-value) + alpha_1 * value;
      return c;
    });

    return toCanvas(imageData);
  })
});

Xml.addFromBuild(MonochromeColors,'MonochromeColors');

// ColorEngineMap
//======================================================================================

export const ColorEngineMap = ColorMap.extend({ typeName: 'ColorEngineMap',

  Properties: {
    brush        : { type: 'Brush'  , def: new ColorEngineBrush({color:Color.White()}), onChange: invalidateOwner }
  },

  invalidate: invalidateOwner,

  getColors( colors = [] ) {
    colors.push( this._brush._color );
    return colors;
  },
  textualize() {
    return this._brush._colors.textualize();
  },

  recolorize: function( image ) {

    const imageData = toImageData(image);

    const color = this._brush._color;

    if (color.colorType == 'CE') {

      var colors = [];

      colors.push(Color.fromRgb(color.ceRGBShad));
      colors.push(Color.fromRgb(color.ceRGBMid));
      colors.push(Color.fromRgb(color.ceRGBHigh));
  
      const rgbGradient = createRGBGradient( colors, [0,0.5,1], 'RGB', Config.GradientColorMap_scaleRes );
  
      modifyImageDataRGBA( imageData, (r,g,b ,a) => {
        const c = rgbGradient(b/255.0);
        c[3] = a;
        return c;
      });
    
    }

    return toCanvas(imageData);
  }
});

Xml.addFromBuild(ColorEngineMap,'ColorEngineMap');


// RGBMap
//======================================================================================
/*

<RGBMap>
  <Entry SourceRGB="FF0000">
    <Color RGB="00FF00"/>
  </Entry>
  <Entry SourceRGB="FF0000">
    <Color RGB="00FF00"/>
  </Entry>
</RGBMap>

*/

const RGBMapEntry = OwnedObject.extend({ typeName: 'RGBMapEntry',

  Properties: {
    sourcePos: { type: 'Point', def: new Point(0.5,0.5), onChange: invalidateOwner },
    sourceRGB: { type: 'string', def: '000000', onChange: invalidateOwner },
    scope: { type: 'number', def: 0.2, onChange: invalidateOwner },
    color: { type: 'Color', def: Color.Black(), owned: true, onChange: invalidateOwner }
  },
  invalidate: invalidateOwner,

  getColors( colors ) {
    colors.push(this._color);
    return colors;
  },
  textualize() {
    return this._sourceRGB + '=>' + this._color.textualize();
  }
});
Xml.addFromBuild(RGBMapEntry,'Entry');

RGBMapEntry.arrayFromXml = function( node ) {
  return Xml.getObjectArray(node,RGBMapEntry,'Entry');
};

const distance = function(a,b) {
  const d_0 = a[0]-b[0], d_1 = a[1]-b[1], d_2 = a[2]-b[2];
  return d_0*d_0 + d_1*d_1 + d_2*d_2;
};

const _0_100 = function(v) {
  return Math.max(0,Math.min(v,100));
};

const _0_360 = function(v) {
  if( v > 360 ) { return v - 360; }
  if( v < 0 ) { return v + 360; }
  return v;
};

// c_factor and f_factor are curves depending on chroma like:
//
//  1         ________________________
//      |   /                        .
//      |  /                         .
//      | /                          .
//  0   |/___________________________._
//      0   10                     100
//
const chromaFactor = function(c) {  // 0 ... 100 =>  0 ... 1
  return c < 10 ? 0.1 * c : 1 ;
};

// Simple, this may be the best one

const result_h_a = function( h,cf, sh,sf, th,tf ) {
  // When cf==1, sf==1 and tf==1 we need to apply the easy formula
  //
  // result = h + ( th - sh );

  // If cf==0 or sf==0 we need to ignore the pixel hue and just
  //                   map directly to the target hue
  //
  // If tf==0 we need to leave the current hue as is

  const ff = cf * sf;

  return ff * h + ( th - ff * sh ) * tf;
};

// Testing different strategies

const result_h_b = function( h,cf, sh,sf, th,tf ) {
  // When cf==1, sf==1 and tf==1 we need to apply the easy formula
  //
  const result_h_0 = h + ( th - sh );

  // If cf==0 or sf==0 we need to ignore the pixel hue and just
  //                   map directly to the target hue

  const ff = cf * sf;
  const result_h_1 = ff * result_h_0 + (1-ff) * th;

  // If tf==0 we need to leave the current hue as is

  return tf * result_h_1 + (1-tf) * h;
};

// Same as b but inverted
const result_h_c = function( h,cf, sh,sf, th,tf ) {
  // When cf==1, sf==1 and tf==1 we need to apply the easy formula
  //
  const result_h_0 = h + ( th - sh );

  // If tf==0 we need to leave the current hue as is

  const result_h_1 = tf * result_h_0 + (1-tf) * h;

  // If cf==0 or sf==0 we need to ignore the pixel hue and just
  //                   map directly to the target hue
  const ff = cf * sf;
  return ff * result_h_1 + (1-ff) * th;
};


const rgbMapFunction = function(entries,toDistanceSpace,fromDistanceSpace,distance/*,interpolateSpace,interpolate*/) {
  const sourceColor = map( entries, e => {
    const c = Color({rgb:e.sourceRGB});
    return toDistanceSpace( c.r(), c.g(), c.b() );
  });
  const toRGB = map( entries, e => e.color.rgbArray );

  const toLCH = map( toRGB, rgb2lch );

  const sourceLCH = map( entries, e => {
    const c = new Color({ rgb: e.sourceRGB });
    return rgb2lch(c.rgbArray);
  });

  const lDiff = map( sourceLCH, (s,i) => toLCH[i][0] - sourceLCH[i][0] );
  const cDiff = map( sourceLCH, (s,i) => toLCH[i][1] - sourceLCH[i][1] );

  // Hue needs to take into account the chroma of the source and the target

  const sourceHue = map( sourceLCH, lch => lch[2] );

  const toHue = map( toLCH, lch => lch[2] );

  const sourceChromaFactor = map( sourceLCH, lch => chromaFactor(lch[1]) );

  const toChromaFactor = map( toLCH, lch => chromaFactor(lch[1]) );

  const configHueShiftFunc = Config.hueShiftFunc;
  const result_h_func = configHueShiftFunc === 'C' ? result_h_c
                    : configHueShiftFunc === 'B' ? result_h_b
                                                 : result_h_a;

  const sourceLimit = map( entries, e => {
    const n = 255 * e.scope;
    return distance(toDistanceSpace(0,0,0),toDistanceSpace(n,n,n));
  });

  return function(r,g,b) {
    let min_k = null, min_distance = Number.MAX_VALUE;
    for( let k = 0, kEnd = sourceColor.length; k < kEnd; ++k ) {
      const ds = toDistanceSpace(r,g,b);
      const sc = sourceColor[k];
      const dist = distance(sc,ds);
      if( dist <= sourceLimit[k] ) {
        if( dist < min_distance ) {
          min_k = k;
          min_distance = dist;
        }
      }
    }

    if( min_k !== null ) {
      const k = min_k;

      // Modify the pixel RGB
      const lch = rgb2lch([r,g,b]);

      const l = lch[0],
            c = lch[1],
            h = lch[2];

      // Modify hue taking into account chroma
      // Do not take into account the source hue if the source color
      // doesn't have enough chroma
      // Leave the chroma as is if the target doesn't have enough chroma
      // Black ( Chroma: 0, Hue: X ) -> White ( Chroma: 0, Hue: X )
      // Black ( Chroma: 0, Hue: X ) -> Green ( Chroma: 1, Hue: G )
      // Green ( Chroma: 1, Hue: G ) -> Black ( Chroma: 1, Hue: G )
      // Green ( Chroma: 1, Hue: G ) -> Red   ( Chroma: 1, Hue: R )

      const sh = sourceHue[k],
            th = toHue[k],

            cf = chromaFactor(c),       // 0...1
            sf = sourceChromaFactor[k], // 0...1
            tf = toChromaFactor[k];     // 0...1

      const result_h = result_h_func( h,cf, sh,sf, th,tf );

      // Direct modification of lightness and chroma
      const result_l = l + lDiff[k];
      const result_c = c + cDiff[k];

      const new_rgb = lch2rgb([ _0_100(result_l) ,
                                _0_100(result_c) ,
                                _0_360(result_h) ]);

      return new_rgb;
    }
    return [r,g,b];
  };
};

export const RGBMap = ColorMap.extend({ typeName: 'RGBMap',

  Properties: {
    entries: { type: 'RGBMapEntry[]', def: [], owned: true, onChange: invalidateOwner },
    distance: { type: 'string', def: 'LAB', onChange:invalidateOwner },
    interpolate: { type: 'string', def: 'LAB', onChange:invalidateOwner }
  },

  get topFigure() {
    const o = this._owner;
    return o && o.topFigure;
  },
  ownerPos_( point ) {
    const f = this.topFigure;
    const bounds = f.globalBounds_();
    return bounds.deinterpolate(point);
  },
  globalPoint_( pos) {
    const f = this.topFigure;
    const bounds = f.globalBounds_();
    return bounds.interpolate(pos);
  },
  invalidate: invalidateOwner,

  getColors( colors = [] ) {
    const this_entries = this._entries;
    //_.forEach(this_colors,function(c) {
    for(let k=0,kEnd=this_entries.length;k<kEnd;++k) {
      const e = this_entries[k];
      e.getColors(colors);
    }//);
    return colors;
  },
  textualize() {
    return this._entries.textualize();
  },

  rgbMapFunction() {
    const rgbMapFunc = rgbMapFunction(this._entries,rgb2[this._distance],null,distance,null);

    const res = Config.RGBMap_RGBCubeRes;

    return res > 0 ? cachedRGBCubeMap(rgbMapFunc,interpolate_rgb,res)
                   : rgbMapFunc;
  },

  recolorize: cachedColorMap(function(image) {
    const imageData = toImageData(image);

    modifyImageDataRGB(imageData,this.rgbMapFunction());

    return toCanvas(imageData);
  })
});
RGBMap.Entry = RGBMapEntry;

Xml.addFromBuild(RGBMap,'RGBMap');

Xml.Parser['RGBMapEntry'  ] = Xml.makeObjectParser(RGBMapEntry);
Xml.Parser['RGBMapEntry[]'] = Xml.makeArrayObjectParser(RGBMapEntry);

ColorMap.fromXml = function( node ) {
  return TaggedColors     .fromXml(node) ||
         GradientColorMap .fromXml(node) ||
         MonochromeColors .fromXml(node) ||
         RGBMap           .fromXml(node) || 
         ColorEngineMap   .fromXml(node) || null ;
};

Xml.Parser['ColorMap'] = Xml.makeObjectParser(ColorMap);



/** WEBPACK FOOTER **
 ** ./src/cdl/style/color-map.js
 **/